import debounce from "lodash/debounce";
import { Store } from "redux";
import { panic, assertNever, DefaultMap, dontAwait, defined, sleep } from "@glideapps/ts-necessities";

import { RootState, getCurrentAppAndStateOrDefaults } from "../store/types";
import { isUserData, UserData, Database } from "common/Database";
import { logInfo, logError } from "common/debug-print";
import { isolateAppDescription } from "common/components/SerializedApp";
import { publishApp, isEditorOfApp } from "../actions/action-publish";
import { showAcceptCancelModal, showBasicActionModal } from "designer/ui/smart/modals/modals";
import {
    updateBuilderApp,
    UpdateBuilderAppResultKind,
    forceBuilderAppLock,
    timeBetweenRefresh,
    tryLockBuilderApp,
    LockResult,
    isSuccessfulUpdateBuilderAppResult,
    isUnknownFailureBuilderAppResult,
} from "common/save-builder-app";
import { navigateToDashboard, updateAppSerial } from "../actions/actions";
import { unloadApps } from "../actions/apps-actions";
import { reloadBrowserWindow } from "./browser-reload";
import { getBuilderAppDatabase, getUnaffiliatedDatabase } from "./web-database";
import { dispatchThunk } from "../actions/action-thunk";
import { getTestBehaviors } from "designer/lib/test-mode";
import { ConditionVariable } from "common/condition-variable";
import { History } from "history";
import { getAppIDFromHistory } from "store/lib";
import { getFeatureSetting } from "common/feature-settings";
import { isSample } from "common/database-strings";
import { isLoadedDefined } from "common/components/component-helpers";
import { getFeatureFlag } from "common/feature-flags";

async function tryEnterConnectionLock(appID: string, targetSet: Set<string>, targetCall: () => void) {
    const db = getUnaffiliatedDatabase();
    await db.enterNetworkConnectionLock();
    // It's possible that someone else acquired the lock
    // while we were trying to acquire the lock. We only
    // need one, so if we've accidentally locked more than
    // we need to, we just immediately exit the excess locks.
    if (targetSet.has(appID)) {
        db.exitNetworkConnectionLock();
    } else {
        targetSet.add(appID);
    }
    targetCall();
}

export enum SaveState {
    Dirty,
    Saving,
    Saved,
}

export const NOTIFY_SAVE_STATE = "notifySaveState";

async function askShouldOverride(): Promise<boolean> {
    return await showAcceptCancelModal(
        "This app is open in another tab",
        "Do you want to open the app in this tab instead? Your other tab will close.",
        "Open app here",
        "Go to dashboard"
    );
}

export enum BuilderLockState {
    None,
    Owned,
    Reader,
}

class MissingSaveStateError extends Error {}

type WatchCallback = (id: string) => void;
class AppsSaver {
    private readonly _appsToSave: Set<string> = new Set();
    private readonly _appsToPublish: Set<string> = new Set();
    private _saving: boolean = false;
    private _savingCV = new ConditionVariable();
    private _shouldSaveAgain: boolean = false;
    private _stopSavingAppIDs: Set<string> = new Set();
    private _outstandingBuilderLockRefresh = false;
    private _notifySaveState = false;
    private _appsSavedResolvers = new DefaultMap<string, (() => void)[]>(() => []);
    private _readOnlyApps: Set<string> = new Set();
    private _watchCallbacks: WatchCallback[] = [];

    constructor(private readonly _store: Store<RootState>, private readonly _history: History<any>) {
        setInterval(() => this.refreshLock(), timeBetweenRefresh);

        if (getTestBehaviors().showSaveState) {
            this._notifySaveState = true;
        }
    }

    public addWatchCallback(cb: WatchCallback): void {
        this._watchCallbacks.push(cb);
    }

    public removeWatchCallback(cb: WatchCallback): void {
        this._watchCallbacks = this._watchCallbacks.filter(c => c !== cb);
    }

    private registerResolverForAppSave(appID: string, fn: () => void): void {
        this._appsSavedResolvers.get(appID).push(fn);
    }

    private resolveForAppSave(appID: string): void {
        const prior = this._appsSavedResolvers.get(appID);
        const resolving = prior.splice(0, prior.length);
        for (const r of resolving) {
            r();
        }
    }

    private async goToDashboard(appID: string, modalMessage: string | undefined): Promise<void> {
        const curState = this._store.getState();
        const appAndState = getCurrentAppAndStateOrDefaults(appID, curState.apps);
        let ownerID = appAndState.userID ?? "0";

        if (isLoadedDefined(curState.userData) && curState.userData !== "loading") {
            if (curState.userData.id === ownerID) {
                // If this is a classic "My Apps" owned app, go back to the dedicated "My Apps" dashboard.
                ownerID = "0";
            }
        }

        if (getFeatureSetting("readOnlyBuilder")) {
            const watch = await showAcceptCancelModal(
                "Another user has begun editing this app",
                "If you'd like, you may continue watching this app, or you may return to the dashboard",
                "Watch",
                "Go to dashboard"
            );
            if (watch) {
                this._readOnlyApps.add(appID);
                for (const cb of this._watchCallbacks) {
                    cb(appID);
                }
            } else {
                this._store.dispatch(unloadApps([appID]));
                dispatchThunk(this._store, navigateToDashboard(ownerID));
            }
        } else {
            if (modalMessage !== undefined) {
                logError("Lock error", modalMessage);
                await showBasicActionModal(
                    "This app was opened in another tab",
                    "If you'd like to continue editing it, please reopen it from your dashboard.",
                    "Ok",
                    false
                );
            }
            this._store.dispatch(unloadApps([appID]));
            dispatchThunk(this._store, navigateToDashboard(ownerID));
        }
    }

    private isUserEditorOfApp(appID: string): boolean {
        const { apps, userData, organizations } = this._store.getState();
        const { userID: ownerUserID } = getCurrentAppAndStateOrDefaults(appID, apps);
        if (!isUserData(userData)) return false;
        return isEditorOfApp(appID, ownerUserID, userData, organizations);
    }

    private shouldSaveApp(appID: string): boolean {
        if (getTestBehaviors().disableAppSaver || this._readOnlyApps.has(appID)) return false;
        return this.isUserEditorOfApp(appID);
    }

    private async refreshLock(): Promise<void> {
        const appID = getAppIDFromHistory(this._history);
        const { userData } = this._store.getState();
        if (
            !isUserData(userData) ||
            appID === undefined ||
            !this.shouldSaveApp(appID) ||
            this._stopSavingAppIDs.has(appID)
        )
            return;

        if (this._outstandingBuilderLockRefresh) return;

        this._outstandingBuilderLockRefresh = true;
        const exit = () => {
            this._outstandingBuilderLockRefresh = false;
        };

        const db = getBuilderAppDatabase(appID);
        let result = await tryLockBuilderApp(db, userData.id, appID, false);
        if (result.result === LockResult.Error) {
            reloadBrowserWindow("locking the builder app failed", result);
            return exit();
        }
        if (result.result === LockResult.CurrentOtherOwned) {
            if (await this.shouldOverrideLock(db, userData.id, appID)) {
                this._stopSavingAppIDs.delete(appID);
                return;
            }
            await this.goToDashboard(appID, "Owned by another session.");
            return exit();
        }
        if (result.result !== LockResult.SelfOwned) {
            // This is to guard against a race condition, namely that we have an old
            // app that needs saving, and we acquire the lock to reload the builder.
            // We must only save once the builder is reloaded, but between acquiring
            // the lock and the reload happening a save could be attempted, which would
            // succeed, since we have that lock.  This disables saving during that time.
            this._stopSavingAppIDs.add(appID);

            result = await tryLockBuilderApp(db, userData.id, appID, true);
            if (result.result === LockResult.SelfOwned || result.result === LockResult.Error) {
                reloadBrowserWindow("locking to disable saving failed", result);
            } else {
                this._stopSavingAppIDs.delete(appID);
                await this.goToDashboard(appID, "Tried acquiring free or old, but couldn't.");
            }
            return exit();
        }
    }

    private async forEach(
        toDo: Set<string>,
        f: (appID: string, userData: UserData, appOwnerUserID: string | undefined, toDo: Set<string>) => Promise<void>,
        retrigger: () => void
    ): Promise<void> {
        const { apps, userData } = this._store.getState();
        if (!isUserData(userData)) {
            // If we're not userData yet, we can't just drop everything on the floor.
            // We have to eventually come around to handling all of this.
            for (const appID of toDo) {
                this.setAppModified(appID);
            }
            return;
        }

        const doing = new Set(toDo);
        toDo.clear();

        await Promise.all(
            Array.from(doing).map(async appID => {
                const { serializedApp, userID } = getCurrentAppAndStateOrDefaults(appID, apps);
                if (serializedApp === undefined) {
                    toDo.add(appID);
                } else {
                    await f(appID, userData, userID, toDo);
                }
            })
        );

        if (toDo.size > 0) retrigger();
    }

    private async shouldOverrideLock(db: Database, userID: string, appID: string): Promise<boolean> {
        if (!getFeatureSetting("askForBuilderLockOverride")) return false;
        if (!(await askShouldOverride())) return false;

        await forceBuilderAppLock(db, userID, appID);
        return true;
    }

    private getAppStateForUpdate = (appID: string) => {
        const { serializedApp, userID, serial } = getCurrentAppAndStateOrDefaults(appID, this._store.getState().apps);
        if (serializedApp === undefined || userID === undefined) {
            throw new MissingSaveStateError();
        }
        const appDescription = isolateAppDescription(serializedApp);
        return { appDescription, serial: defined(serial), userID: userID };
    };

    private updateAppSerial = (appID: string, nextSerial: number) => {
        dispatchThunk(this._store, updateAppSerial(appID, nextSerial));
    };

    // Will return `true` if we need to return to the dashboard due to a failed
    // save of the current app.
    private async saveApp(
        appID: string,
        userData: UserData,
        currentAppID: string | undefined,
        toDo: Set<string>
    ): Promise<boolean> {
        if (this._stopSavingAppIDs.has(appID) || this._readOnlyApps.has(appID)) return false;

        if (this._notifySaveState) {
            // setTimeout works around issues with redux
            setTimeout(
                () =>
                    PubSub.publishSync(NOTIFY_SAVE_STATE, {
                        appID,
                        saveState: SaveState.Saving,
                    }),
                0
            );
        }

        const startTime = Date.now();
        const db = getBuilderAppDatabase(appID);

        if (getFeatureFlag("stallAppSaver")) {
            await sleep(10000);
        }
        const result = await updateBuilderApp(db, appID, this.getAppStateForUpdate, this.updateAppSerial);
        const stopTime = Date.now();
        const success = isSuccessfulUpdateBuilderAppResult(result);

        if (this._notifySaveState) {
            // setTimeout works around issues with redux
            setTimeout(
                () =>
                    PubSub.publishSync(NOTIFY_SAVE_STATE, {
                        appID,
                        saveState: SaveState.Saved,
                    }),
                0
            );
        }

        logInfo(`saved app ${appID} in ${stopTime - startTime} ms - success ${success}`);

        if (isUnknownFailureBuilderAppResult(result) && result.exception instanceof MissingSaveStateError) {
            // If the state of the app to publish disappeared underneath us,
            // this isn't an error that requires us to push back to the dashboard
            // (for all we know, the user logged out). Instead, we'll just have
            // to reconsider the app for saving later.
            toDo.add(appID);
            return false;
        }

        if (result.kind !== UpdateBuilderAppResultKind.Success && appID === currentAppID) {
            if (result.kind === UpdateBuilderAppResultKind.NoLock) {
                if (await this.shouldOverrideLock(db, userData.id, appID)) {
                    return await this.saveApp(appID, userData, currentAppID, toDo);
                }
                return true;
            } else if (
                result.kind === UpdateBuilderAppResultKind.SizeLimit ||
                result.kind === UpdateBuilderAppResultKind.UnknownFailure
            ) {
                const description =
                    result.kind === UpdateBuilderAppResultKind.SizeLimit
                        ? "Right now app data is limited to 1MB. We know, that’s less than floppy disk ;) We’ll fix it soon."
                        : "There was an error saving your app.";
                dontAwait(showBasicActionModal("Could not save your app.", description, "OK"));
            } else if (result.kind === UpdateBuilderAppResultKind.StaleSerial) {
                // With a stale serial, we don't have a choice except to fully back out.
                return true;
            } else {
                return assertNever(result.kind);
            }
        }

        return false;
    }

    public async saveAppIfNecessary(appID: string): Promise<void> {
        if (!this._appsToSave.has(appID) || this._readOnlyApps.has(appID)) return;

        this._appsToSave.delete(appID);

        const { apps, userData } = this._store.getState();
        if (!isUserData(userData)) return;

        const { serializedApp } = getCurrentAppAndStateOrDefaults(appID, apps);
        if (serializedApp === undefined) return;

        await this.saveApp(appID, userData, undefined, new Set());
    }

    private async saveState(): Promise<void> {
        if (this._saving) {
            this._shouldSaveAgain = true;
            return;
        }

        const currentAppID = getAppIDFromHistory(this._history);

        if (currentAppID !== undefined && this.getAppIsReadOnly(currentAppID)) {
            return;
        }

        this._saving = true;
        do {
            let returnToDashboard = false;
            this._shouldSaveAgain = false;

            await this.forEach(
                this._appsToSave,
                async (appID, userData, _, toDo) => {
                    try {
                        if (!this.shouldSaveApp(appID)) return;
                        if (await this.saveApp(appID, userData, currentAppID, toDo)) {
                            returnToDashboard = true;
                        }
                    } finally {
                        getUnaffiliatedDatabase().exitNetworkConnectionLock();
                        this.resolveForAppSave(appID);
                    }
                },
                this.saveStateDebounced
            );

            if (currentAppID !== undefined && returnToDashboard) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.goToDashboard(currentAppID, "Failed to save.");
            }
        } while (this._shouldSaveAgain);

        this._saving = false;
        this._savingCV.notifyAll();
    }

    private async publishApps(): Promise<void> {
        const promises: Promise<void>[] = [];
        await this.forEach(
            this._appsToPublish,
            async appID => {
                if (this.getAppIsReadOnly(appID)) {
                    return;
                }
                promises.push(
                    dispatchThunk(
                        this._store,
                        publishApp(appID, false, () => {
                            getUnaffiliatedDatabase().exitNetworkConnectionLock();
                        })
                    )
                );
            },
            this.publishAppsDebounced
        );
        await Promise.all(promises);
    }

    private saveStateDebounced = debounce(
        () =>
            setTimeout(async () => {
                await this.saveState();
            }, 0),
        1000
    );
    private publishAppsDebounced = debounce(() => setTimeout(async () => await this.publishApps(), 0), 5000);

    public markAppToBePublished(appID: string): void {
        if (this.getAppIsReadOnly(appID)) {
            return;
        }
        dontAwait(
            tryEnterConnectionLock(appID, this._appsToPublish, () => {
                this.publishAppsDebounced();
            })
        );
    }

    public setAppModified(appID: string): void {
        if (this.getAppIsReadOnly(appID)) {
            return;
        }
        if (this._notifySaveState) {
            // setTimeout works around issues with redux
            setTimeout(
                () =>
                    PubSub.publishSync(NOTIFY_SAVE_STATE, {
                        appID,
                        saveState: SaveState.Dirty,
                    }),
                0
            );
        }

        dontAwait(
            tryEnterConnectionLock(appID, this._appsToSave, () => {
                this.saveStateDebounced();
            })
        );

        this.markAppToBePublished(appID);
    }

    public getAppIsReadOnly(appID: string): boolean {
        return this._readOnlyApps.has(appID);
    }

    public async forceSaveApp(appID: string): Promise<void> {
        if (this.getAppIsReadOnly(appID)) {
            return;
        }
        return new Promise(async (resolve, reject) => {
            try {
                while (this._saving) {
                    await this._savingCV.wait();
                }
                this.registerResolverForAppSave(appID, resolve);
                await tryEnterConnectionLock(appID, this._appsToSave, () => {
                    this.saveStateDebounced();
                });
            } catch (e: unknown) {
                reject(e);
            }
        });
    }

    public async lockOrAskToGoReadOnly(db: Database, userID: string, appID: string): Promise<BuilderLockState> {
        if (isSample(appID)) return BuilderLockState.Reader;

        this._stopSavingAppIDs.add(appID);
        try {
            const { result } = await tryLockBuilderApp(db, userID, appID, true);
            if (result === LockResult.SelfOwned) return BuilderLockState.Owned;
            const isEditor = this.isUserEditorOfApp(appID);
            if (isEditor && !getFeatureSetting("readOnlyBuilder")) {
                await this.goToDashboard(appID, "Could not acquire lock");
                return BuilderLockState.Reader;
            }
            let goReader: boolean;
            if (!isEditor) {
                goReader = true;
                if (!isSample(appID)) {
                    await showBasicActionModal(
                        "Read-Only",
                        "You're in read-only mode.  Any changes you make won't be saved.",
                        "OK"
                    );
                }
            } else {
                goReader = await showAcceptCancelModal(
                    "Go read-only?",
                    "Someone else is currently editing this app, would you like to enter in read-only mode?",
                    "Watch",
                    "Take Over"
                );
            }
            if (goReader) {
                this._readOnlyApps.add(appID);
                return BuilderLockState.Reader;
            }
            this._readOnlyApps.delete(appID);
            await forceBuilderAppLock(db, userID, appID);
            return BuilderLockState.Owned;
        } catch (e: unknown) {
            logError("Errored when locking or asking user to force lock", e);
            return BuilderLockState.None;
        } finally {
            this._stopSavingAppIDs.delete(appID);
        }
    }

    public async lockOrAskToForceBuilderAppLock(
        db: Database,
        userID: string,
        appID: string,
        goToDashboardIfUnsuccessful: boolean
    ): Promise<boolean> {
        if (!this.shouldSaveApp(appID)) return true;

        // We need to prevent trying to save the app while the modal
        // is open.
        this._stopSavingAppIDs.add(appID);

        try {
            const { result } = await tryLockBuilderApp(db, userID, appID, true);
            if (result === LockResult.Error) {
                reloadBrowserWindow("locking the builder app failed", result);
                return false;
            }
            if (result === LockResult.SelfOwned) {
                this._stopSavingAppIDs.delete(appID);
                return true;
            }

            if (getTestBehaviors().bypassAppLock || (await askShouldOverride())) {
                await forceBuilderAppLock(db, userID, appID);
                this._stopSavingAppIDs.delete(appID);
                return true;
            } else {
                this._stopSavingAppIDs.delete(appID);
                if (goToDashboardIfUnsuccessful) {
                    await this.goToDashboard(appID, undefined);
                }
                return false;
            }
        } catch (e: unknown) {
            logError("Errored when locking or asking user to force lock", e);
            this._stopSavingAppIDs.delete(appID);
            return false;
        }
    }

    public isSaving(): boolean {
        return this._saving || this._appsToSave.size > 0;
    }
}

let appsSaver: AppsSaver | undefined;

window.addEventListener("beforeunload", ev => {
    if (appsSaver === undefined) return;
    if (!appsSaver.isSaving()) return;
    ev.preventDefault();
    // Nobody actually uses this for display, but it needs to be
    // explicitly set on Chrome or else we won't be able to cancel
    // the tab closing.
    ev.returnValue = "";
});

export function initAppsSaver(store: Store<RootState>, history: History<any>): void {
    appsSaver = new AppsSaver(store, history);
}

export function getAppsSaver(): AppsSaver {
    if (appsSaver === undefined) {
        return panic("apps saver not initialized");
    }
    return appsSaver;
}
